import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  TodayIcon,
} from '@blocksuite/icons/lit';
import { html, type TemplateResult } from 'lit';

import {
  temporalToolbarButtonStyle,
  temporalToolbarGroupStyle,
  temporalToolbarIconButtonStyle,
  temporalToolbarRangeGroupStyle,
  temporalToolbarRangeTitleStyle,
} from './temporal-toolbar-css.js';

export const TEMPORAL_TOOLBAR_ICON_SIZE = {
  height: '16px',
  width: '16px',
} as const;

export type TemporalToolbarModeOption<T extends string> = {
  ariaLabel: string;
  disabled?: boolean;
  key: T;
  label: string;
};

export function renderTemporalModeGroup<T extends string>({
  activeMode,
  buttonClass = temporalToolbarButtonStyle,
  groupClass = temporalToolbarGroupStyle,
  onModeChange,
  options,
}: {
  activeMode: T;
  buttonClass?: string;
  groupClass?: string;
  onModeChange: (mode: T) => void;
  options: ReadonlyArray<TemporalToolbarModeOption<T>>;
}): TemplateResult {
  return html`<div class=${groupClass}>
    ${options.map(
      option =>
        html`<button
          class=${buttonClass}
          aria-label=${option.ariaLabel}
          aria-pressed=${activeMode === option.key}
          data-active=${activeMode === option.key}
          ?disabled=${option.disabled}
          @click=${() => {
            if (!option.disabled) onModeChange(option.key);
          }}
        >
          ${option.label}
        </button>`
    )}
  </div>`;
}

export function renderTemporalRangeControls({
  buttonClass = temporalToolbarButtonStyle,
  groupClass = temporalToolbarRangeGroupStyle,
  iconButtonClass = temporalToolbarIconButtonStyle,
  nextDisabled = false,
  nextLabel,
  onNext,
  onPrevious,
  onToday,
  previousDisabled = false,
  previousLabel,
  rangeTitle,
  rangeTitleClass = temporalToolbarRangeTitleStyle,
  todayAriaLabel,
  todayButtonExtraClass,
  todayDisabled = false,
  todayLabel,
}: {
  buttonClass?: string;
  groupClass?: string;
  iconButtonClass?: string;
  nextDisabled?: boolean;
  nextLabel: string;
  onNext: () => void;
  onPrevious: () => void;
  onToday: () => void;
  previousDisabled?: boolean;
  previousLabel: string;
  rangeTitle?: string | TemplateResult;
  rangeTitleClass?: string;
  todayAriaLabel: string;
  todayButtonExtraClass?: string;
  todayDisabled?: boolean;
  todayLabel: string;
}): TemplateResult {
  const todayClass = todayButtonExtraClass
    ? `${buttonClass} ${todayButtonExtraClass}`
    : buttonClass;

  return html`<div class=${groupClass}>
    <button
      class=${iconButtonClass}
      data-temporal-range-part="previous"
      aria-label=${previousLabel}
      ?disabled=${previousDisabled}
      @click=${onPrevious}
    >
      ${ArrowLeftSmallIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
    </button>
    <button
      class=${todayClass}
      data-temporal-range-part="today"
      aria-label=${todayAriaLabel}
      ?disabled=${todayDisabled}
      @click=${onToday}
    >
      ${TodayIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
      <span>${todayLabel}</span>
    </button>
    <button
      class=${iconButtonClass}
      data-temporal-range-part="next"
      aria-label=${nextLabel}
      ?disabled=${nextDisabled}
      @click=${onNext}
    >
      ${ArrowRightSmallIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}
    </button>
    ${rangeTitle
      ? html`<div class=${rangeTitleClass} data-temporal-range-part="title">
          ${rangeTitle}
        </div>`
      : null}
  </div>`;
}
